import { useCallback } from 'react';

import { ERROR, GA4_EVENT_EXCEPTION, ERROR_TYPE_CLIENT, ERROR_TYPE_SERVER, ERROR_TYPE_THIRD_PARTY, ERROR_TYPE_GENERIC_ERROR, NOT_APPLICABLE } from '@/data/constants/gtm';
import { TransactionErrorResponse } from '@/data/types/Basic';

import { triggerGA4Event } from 'presentation/events/services/ga4EcommerceService';
import { GA4ExceptionPayload } from 'presentation/events/types/ga4Types';

enum ErrorStatusCode {
	status408 = 408,
	status500 = 500,
	status502 = 502,
	status503 = 503,
	status504 = 504,
	status505 = 505,
}

const errorList = [
	'error_fleet',
	'error_server',
	'som_service_not_available',
	'_ERR_INVENTORY_GENERIC_SYSTEM_EXCEPTION',
];

const errorStatus_errorType_fatal: Record<number, string[]> = {
	400: [ERROR_TYPE_CLIENT, "false"],
	408: [ERROR_TYPE_CLIENT, "false"],
	500: [ERROR_TYPE_SERVER, "true"],
	502: [ERROR_TYPE_SERVER, "true"],
	503: [ERROR_TYPE_SERVER, "true"],
	504: [ERROR_TYPE_SERVER, "true"],
	505: [ERROR_TYPE_CLIENT, "false"],
};

const errorKey_errorType_fatal: Record<string, string[]> = {
	'error_fleet': [ERROR_TYPE_SERVER, "true"],
	'error_server': [ERROR_TYPE_SERVER, "true"],
	'som_service_not_available': [ERROR_TYPE_THIRD_PARTY, "false"],
	'_ERR_INVENTORY_GENERIC_SYSTEM_EXCEPTION': [ERROR_TYPE_THIRD_PARTY, "false"],
	'_GENERIC_ERROR': [ERROR_TYPE_GENERIC_ERROR, NOT_APPLICABLE],
};

const isNotStatusCode = (status: number) =>
	status !== ErrorStatusCode.status408 &&
	status !== ErrorStatusCode.status500 &&
	status !== ErrorStatusCode.status502 &&
	status !== ErrorStatusCode.status503 &&
	status !== ErrorStatusCode.status504 &&
	status !== ErrorStatusCode.status505;

const isErrorType = (keyError: string) => errorList.includes(keyError);

const isTransactionErrorResponse = (error: unknown): error is TransactionErrorResponse =>
	!!error &&
	typeof error === 'object' &&
	'status' in error &&
	'statusText' in error &&
	'url' in error &&
	'error' in error;

export const useTrackException = () =>
	useCallback((error: unknown, isDialogView?: boolean, endPoint?: string) => {
		// This is called from the catch blocks of other tracking hooks, whose whole
		// purpose is to never let a tracking failure interrupt the checkout flow, so
		// a failure in the exception-reporting itself (e.g. GTM/dataLayer not ready)
		// must be swallowed here rather than escaping and rejecting the caller.
		try {
			if (isTransactionErrorResponse(error)) {
				const { status, statusText, url, error: _error } = error;
				const errorKey = _error.errors?.[0]?.errorKey || '';
				const errorMessage = _error.errors?.[0]?.errorMessage || '';

				let fatal: string = "";
				let errorType: string = "";
				if (errorKey_errorType_fatal[errorKey]) {
					errorType = errorKey_errorType_fatal[errorKey][0];
					fatal = errorKey_errorType_fatal[errorKey][1];
				}
				if (errorStatus_errorType_fatal[status]) {
					errorType = errorStatus_errorType_fatal[status][0];
					fatal = errorStatus_errorType_fatal[status][1];
				}

				if (isNotStatusCode(status) || (isNotStatusCode(status) && !isErrorType(errorKey)))
					return;

				const payload: GA4ExceptionPayload = {
					event: GA4_EVENT_EXCEPTION,
					event_category: ERROR,
					error_code: `${status}`,
					error_message: errorMessage ? errorMessage : `${statusText}`,
					url_error: url,
					page_location: decodeURI(window.location.href),
					element_location: isDialogView ? 'modal' : 'page',
					end_point: endPoint ? window.location.origin + '/' + endPoint : NOT_APPLICABLE,
					error_type: errorType ? errorType : ERROR_TYPE_SERVER,
					fatal: fatal,
				};

				triggerGA4Event(payload);
				return;
			}

			let description = 'Unknown error';

			if (error instanceof Error) {
				description = error.message;
			}

			if (typeof error === 'string') {
				description = error;
			}

			triggerGA4Event({
				event: GA4_EVENT_EXCEPTION,
				event_category: ERROR,
				error_code: NOT_APPLICABLE,
				error_message: description,
				url_error: NOT_APPLICABLE,
				page_location: decodeURI(window.location.href),
				element_location: isDialogView ? 'modal' : 'page',
				end_point: endPoint ? window.location.origin + '/' + endPoint : NOT_APPLICABLE,
				error_type: ERROR_TYPE_CLIENT,
				fatal: 'false',
			});
		} catch (trackingError) {
			console.error('[analytics]: Error reporting tracked exception', trackingError);
		}
	}, []);
